import React from 'react';
import { FaEdit, FaTrash, FaEllipsisV } from 'react-icons/fa';

interface Column<T> {
  header: string;
  accessor: keyof T | ((item: T) => React.ReactNode);
  className?: string;
}

interface AdminTableProps<T> {
  columns: Column<T>[];
  data: T[];
  onEdit?: (item: T) => void;
  onDelete?: (item: T) => void;
  onConfirmDelete?: (item: T) => void;
}

export function AdminTable<T extends { id: string | number }>({
  columns,
  data,
  onEdit,
  onDelete,
  onConfirmDelete,
}: AdminTableProps<T>) {
  if (data.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FaEllipsisV className="text-gray-400 text-xl" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Veri bulunamadı</h3>
        <p className="text-gray-500">
          Henüz hiç kayıt bulunmuyor. Yeni bir kayıt ekleyerek başlayın.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100">
              {columns.map((column, index) => (
                <th
                  key={index}
                  className={`px-6 py-4 sm:px-2 sm:py-2 text-left text-xs font-bold text-gray-600 uppercase tracking-wide ${column.className}`}
                >
                  {column.header}
                </th>
              ))}
              {(onEdit || onDelete || onConfirmDelete) && (
                <th className="px-6 py-4 sm:px-2 sm:py-2 text-right text-xs font-bold text-gray-600 uppercase tracking-wide">
                  İşlemler
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {data.map((item, itemIndex) => (
              <tr
                key={item.id}
                className={`
                  hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 
                  transition-all duration-200 group
                  ${itemIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}
                `}
              >
                {columns.map((column, index) => (
                  <td key={index} className={`px-6 py-4 sm:px-2 sm:py-2 text-sm sm:text-xs text-gray-900 ${column.className}`}>
                    {typeof column.accessor === 'function'
                      ? column.accessor(item)
                      : String(item[column.accessor])}
                  </td>
                ))}
                {(onEdit || onDelete || onConfirmDelete) && (
                  <td className="px-6 py-4 sm:px-2 sm:py-2 text-right">
                    <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-all duration-200">
                      {onEdit && (
                        <button
                          onClick={() => onEdit(item)}
                          className="p-2 sm:p-3 text-blue-500 hover:text-white hover:bg-blue-500 rounded-lg transition-all duration-200 transform hover:scale-110 shadow-md hover:shadow-lg text-base sm:text-lg"
                          title="Düzenle"
                        >
                          <FaEdit className="text-sm sm:text-base" />
                        </button>
                      )}
                      {(onDelete || onConfirmDelete) && (
                        <button
                          onClick={() => {
                            if (onConfirmDelete) {
                              onConfirmDelete(item);
                            } else if (
                              onDelete &&
                              window.confirm('Bu kaydı silmek istediğinizden emin misiniz?')
                            ) {
                              onDelete(item);
                            }
                          }}
                          className="p-2 sm:p-3 text-red-500 hover:text-white hover:bg-red-500 rounded-lg transition-all duration-200 transform hover:scale-110 shadow-md hover:shadow-lg text-base sm:text-lg"
                          title="Sil"
                        >
                          <FaTrash className="text-sm sm:text-base" />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
